import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  AppBar,
  Toolbar,
  Avatar,
  IconButton,
  Alert,
  List,
  ListItem,
  ListItemText,
  Chip,
  CircularProgress,
  Tabs,
  Tab,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Grid,
  Fab,
  Menu,
  MenuList,
  ListItemIcon,
  OutlinedInput,
  Snackbar
} from '@mui/material';
import {
  Logout,
  CalendarToday,
  FamilyRestroom,
  Event,
  CheckCircle,
  Warning,
  ViewModule,
  ViewList,
  Add,
  FilterList,
  Person,
  Restaurant,
  Home,
  Sports,
  School,
  Work,
  Delete,
  CameraAlt
} from '@mui/icons-material';
import { Calendar, momentLocalizer } from 'react-big-calendar';
import moment from 'moment';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useFamily } from '../contexts/FamilyContext';
import { 
  collection, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query,
  where, 
  orderBy,
  onSnapshot
} from 'firebase/firestore';
import { db } from '../firebase';
import { Tooltip } from '@mui/material';

// Configure moment for react-big-calendar
const localizer = momentLocalizer(moment);

// Event types with colors
const EVENT_TYPES = {
  chore: { label: 'Chore', color: '#1976d2', icon: <Home /> },
  appointment: { label: 'Appointment', color: '#388e3c', icon: <Event /> },
  meal: { label: 'Meal', color: '#f57c00', icon: <Restaurant /> },
  activity: { label: 'Activity', color: '#7b1fa2', icon: <Sports /> },
  school: { label: 'School', color: '#d32f2f', icon: <School /> },
  work: { label: 'Work', color: '#455a64', icon: <Work /> }
};

function CalendarPage() {
  const { userProfile, logout } = useAuth();
  const { tasks, familyMembers } = useFamily();
  const navigate = useNavigate();
  
  // State
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({
    title: '',
    start: new Date(),
    end: new Date(Date.now() + 60 * 60 * 1000), // 1 hour later
    eventType: 'chore',
    assignedTo: [],
    allowance: 0,
    description: '',
    status: 'pending',
    relatedChoreId: null,
    screenTimeEnabled: false, // Lock device during this event (Android only)
    recurring: {
      enabled: false,
      frequency: 'weekly', // daily, weekly, monthly
      interval: 1, // every 1 week, 2 weeks, etc.
      endDate: null, // when to stop recurring
      daysOfWeek: [] // for weekly: [0,1,2,3,4,5,6] (Sunday=0)
    }
  });
  const [filters, setFilters] = useState({
    members: [],
    eventTypes: [],
    showCompleted: true
  });
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadEvents();
  }, [userProfile]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    // Auto-sync tasks to events
    syncTasksToEvents();
  }, [tasks]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadEvents = async () => {
    if (!userProfile?.familyId) return;
    
    try {
      const eventsRef = collection(db, 'events');
      const q = query(
        eventsRef,
        where('familyId', '==', userProfile.familyId),
        orderBy('start', 'asc')
      );
      
      const unsubscribe = onSnapshot(q, (snapshot) => {
        const eventsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          start: doc.data().start.toDate(),
          end: doc.data().end.toDate()
        }));
        setEvents(eventsData);
        setLoading(false);
      });

      return () => unsubscribe();
    } catch (error) {
      console.error('Error loading events:', error);
      setLoading(false);
    }
  };

  const syncTasksToEvents = async () => {
    if (!userProfile?.familyId || !tasks.length) return;

    try {
      for (const task of tasks) {
        if (task.status === 'pending' && !task.calendarEventId) {
          // Create event for this task (use today if no dueDate)
          const dueDate = task.dueDate ? new Date(task.dueDate) : new Date();
          // Skip if date is invalid
          if (isNaN(dueDate.getTime())) continue;;
          const eventData = {
            familyId: userProfile.familyId,
            title: task.title,
            start: dueDate,
            end: new Date(dueDate.getTime() + 60 * 60 * 1000), // 1 hour duration
            eventType: 'chore',
            assignedTo: Array.isArray(task.assignedTo) ? task.assignedTo : [task.assignedTo],
            description: task.description || '',
            status: 'pending',
            relatedChoreId: task.id,
            isRecurring: task.isRecurring || false,
            createdAt: new Date().toISOString()
          };

          const docRef = await addDoc(collection(db, 'events'), eventData);
          
          // Update task with calendar event ID
          await updateDoc(doc(db, 'tasks', task.id), {
            calendarEventId: docRef.id
          });
        }
      }
    } catch (error) {
      console.error('Error syncing tasks to events:', error);
    }
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/auth');
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const handleBackToDashboard = () => {
    if (userProfile?.role === 'parent') {
      navigate('/parent');
    } else {
      navigate('/child');
    }
  };

  const handleSelectSlot = ({ start, end }) => {
    setNewEvent({
      ...newEvent,
      start: start,
      end: end
    });
    setEventDialogOpen(true);
  };

  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    setNewEvent(event);
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    try {
      // Clean event data - convert Timestamps to Dates, remove undefined
      const cleanEvent = { ...newEvent };
      
      // Ensure dates are proper Date objects (not Timestamps)
      if (cleanEvent.start?.toDate) cleanEvent.start = cleanEvent.start.toDate();
      if (cleanEvent.end?.toDate) cleanEvent.end = cleanEvent.end.toDate();
      if (cleanEvent.start && !(cleanEvent.start instanceof Date)) cleanEvent.start = new Date(cleanEvent.start);
      if (cleanEvent.end && !(cleanEvent.end instanceof Date)) cleanEvent.end = new Date(cleanEvent.end);
      
      // Remove undefined/null fields
      Object.keys(cleanEvent).forEach(key => {
        if (cleanEvent[key] === undefined || cleanEvent[key] === null) delete cleanEvent[key];
      });
      
      const eventData = {
        ...cleanEvent,
        familyId: userProfile.familyId,
        updatedAt: new Date().toISOString()
      };

      if (selectedEvent) {
        // Update existing event - only update specific fields to avoid conflicts
        const updateData = {
          title: eventData.title,
          description: eventData.description || '',
          start: eventData.start,
          end: eventData.end,
          eventType: eventData.eventType,
          assignedTo: eventData.assignedTo || [],
          status: eventData.status || 'pending',
          isRecurring: eventData.isRecurring || false,
          updatedAt: eventData.updatedAt
        };
        await updateDoc(doc(db, 'events', selectedEvent.id), updateData);
        setToast({ open: true, message: 'Event updated!', severity: 'success' });
      } else {
        // Create new event
        eventData.createdAt = new Date().toISOString();
        await addDoc(collection(db, 'events'), eventData);
        setToast({ open: true, message: 'Event created!', severity: 'success' });
      }

      setEventDialogOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
      setToast({ 
        open: true, 
        message: 'Failed to save: ' + error.message, 
        severity: 'error' 
      });
      // Don't redirect on error - let user try again
    }
  };

  const handleDeleteEvent = async () => {
    if (!selectedEvent) return;
    
    try {
      await deleteDoc(doc(db, 'events', selectedEvent.id));
      setEventDialogOpen(false);
      setSelectedEvent(null);
    } catch (error) {
      console.error('Error deleting event:', error);
    }
  };

  const handleEventDrop = async ({ event, start, end }) => {
    try {
      await updateDoc(doc(db, 'events', event.id), {
        start: start,
        end: end,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating event:', error);
    }
  };

  // Filter events based on current filters
  const filteredEvents = events.filter(event => {
    if (filters.members.length > 0 && !event.assignedTo.some(id => filters.members.includes(id))) {
      return false;
    }
    if (filters.eventTypes.length > 0 && !filters.eventTypes.includes(event.eventType)) {
      return false;
    }
    if (!filters.showCompleted && event.status === 'complete') {
      return false;
    }
    return true;
  });

  // Get upcoming tasks for list view
  const upcomingTasks = tasks
    .filter(task => task && task.id && task.title && task.status === 'pending')
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate))
    .slice(0, 10);

  const formatTaskDate = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return 'Tomorrow';
    } else {
      return date.toLocaleDateString();
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ bgcolor: 'primary.main' }}>
        <Toolbar>
          <IconButton color="inherit" onClick={handleBackToDashboard}>
            <FamilyRestroom />
          </IconButton>
          <CalendarToday sx={{ ml: 2, mr: 2 }} />
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            Family Calendar
          </Typography>
          
          {/* Filter Button */}
          <IconButton 
            color="inherit" 
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
          >
            <FilterList />
          </IconButton>
          
          <Avatar sx={{ bgcolor: 'secondary.main', mr: 2 }}>
            {userProfile?.displayName?.charAt(0)}
          </Avatar>
          <IconButton color="inherit" onClick={handleLogout}>
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Filter Menu */}
      <Menu
        anchorEl={filterMenuAnchor}
        open={Boolean(filterMenuAnchor)}
        onClose={() => setFilterMenuAnchor(null)}
      >
        <MenuList>
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold' }}>
            Filter by Member
          </Typography>
          {familyMembers.map((member) => (
            <MenuItem
              key={member.id}
              onClick={() => {
                const newMembers = filters.members.includes(member.id)
                  ? filters.members.filter(id => id !== member.id)
                  : [...filters.members, member.id];
                setFilters({ ...filters, members: newMembers });
              }}
            >
              <ListItemIcon>
                <Person />
              </ListItemIcon>
              <ListItemText primary={member.displayName} />
            </MenuItem>
          ))}
          
          <Typography variant="subtitle2" sx={{ px: 2, py: 1, fontWeight: 'bold', mt: 1 }}>
            Filter by Type
          </Typography>
          {Object.entries(EVENT_TYPES).map(([type, config]) => (
            <MenuItem
              key={type}
              onClick={() => {
                const newTypes = filters.eventTypes.includes(type)
                  ? filters.eventTypes.filter(t => t !== type)
                  : [...filters.eventTypes, type];
                setFilters({ ...filters, eventTypes: newTypes });
              }}
            >
              <ListItemIcon sx={{ color: config.color }}>
                {config.icon}
              </ListItemIcon>
              <ListItemText primary={config.label} />
            </MenuItem>
          ))}
          
          <MenuItem
            onClick={() => setFilters({ ...filters, showCompleted: !filters.showCompleted })}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={filters.showCompleted}
                  onChange={() => setFilters({ ...filters, showCompleted: !filters.showCompleted })}
                />
              }
              label="Show Completed"
            />
          </MenuItem>
        </MenuList>
      </Menu>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Calendar Views */}
        <Card>
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab icon={<ViewModule />} label="Calendar View" />
            <Tab icon={<ViewList />} label="Task List" />
          </Tabs>

          <CardContent>
            {tabValue === 0 && (
              <Box sx={{ height: { xs: '400px', sm: '500px', md: '600px' } }}>
                <Calendar
                  localizer={localizer}
                  events={filteredEvents}
                  startAccessor="start"
                  endAccessor="end"
                  onSelectSlot={handleSelectSlot}
                  onSelectEvent={handleSelectEvent}
                  onEventDrop={handleEventDrop}
                  selectable
                  resizable
                  style={{ height: '100%' }}
                  eventPropGetter={(event) => ({
                    style: {
                      backgroundColor: EVENT_TYPES[event.eventType]?.color || '#1976d2',
                      borderRadius: '4px',
                      opacity: event.status === 'complete' ? 0.6 : 1,
                      color: 'white',
                      border: event.screenTimeEnabled ? '2px solid #fff' : 'none',
                      fontSize: '14px'
                    }
                  })}
                  components={{
                    event: ({ event }) => (
                      <span style={{ display: 'flex', alignItems: 'center', gap: 2, overflow: 'hidden' }}>
                        {event.screenTimeEnabled && <span title="Screen time lock active" style={{ fontSize: '10px', flexShrink: 0 }}>🔒</span>}
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{event.title}</span>
                      </span>
                    )
                  }}
                  views={['month', 'week', 'day']}
                  defaultView="month"
                />
              </Box>
            )}

            {tabValue === 1 && (
              <Box>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Event color="primary" />
              Upcoming Tasks
            </Typography>
            
            {upcomingTasks.length === 0 ? (
              <Alert severity="success">
                🎉 No pending tasks! Your family is all caught up!
              </Alert>
            ) : (
              <List>
                {upcomingTasks.map((task) => (
                  <ListItem key={task.id} divider>
                    <ListItemText
                      primary={task.title}
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {task.description}
                          </Typography>
                          <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                            <Chip 
                              label={formatTaskDate(task.dueDate)} 
                              size="small" 
                              color="primary" 
                              variant="outlined"
                            />
                            {task.allowance > 0 && (
                              <Chip 
                                label={`$${task.allowance}`} 
                                size="small" 
                                color="success" 
                                variant="outlined"
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card sx={{ mt: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              How the Native Calendar Works
            </Typography>
            <Typography variant="body2" paragraph>
              Your new native calendar provides:
            </Typography>
            <Box component="ul" sx={{ pl: 2 }}>
              <li>Click any day to create new events</li>
              <li>Drag and drop events to reschedule</li>
              <li>Color-coded events by type (chores, meals, appointments)</li>
              <li>Filter by family member or event type</li>
              <li>Automatic sync with your tasks</li>
              <li>Interactive event details and editing</li>
            </Box>
          </CardContent>
        </Card>
      </Container>

      {/* Enhanced Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          bgcolor: selectedEvent ? EVENT_TYPES[selectedEvent.eventType]?.color || 'primary.main' : 'primary.main',
          color: 'white'
        }}>
          <Box sx={{ color: 'white' }}>
            {selectedEvent ? EVENT_TYPES[selectedEvent.eventType]?.icon : <Event />}
          </Box>
          <Box>
            <Typography variant="h6">
              {selectedEvent ? 'Event Details' : 'Create New Event'}
            </Typography>
            {selectedEvent && (
              <Typography variant="body2" sx={{ opacity: 0.9 }}>
                {moment(selectedEvent.start).format('MMMM D, YYYY • h:mm A')}
              </Typography>
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent sx={{ p: 3, pt: 4, mt: 2 }}>
          {/* Event Status & Actions for Chores */}
          {selectedEvent && selectedEvent.eventType === 'chore' && (
            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CheckCircle color={selectedEvent.status === 'complete' ? 'success' : 'disabled'} />
                    Status: {selectedEvent.status === 'complete' ? 'Completed' : 'Pending'}
                  </Typography>
                  {selectedEvent.allowance > 0 && (
                    <Chip 
                      label={`$${selectedEvent.allowance} Reward`} 
                      color="success" 
                      variant="outlined"
                    />
                  )}
                </Box>
                
                {userProfile?.role === 'parent' && selectedEvent.status !== 'complete' && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="success"
                      onClick={async () => {
                        await updateDoc(doc(db, 'events', selectedEvent.id), {
                          status: 'complete',
                          completedAt: new Date().toISOString(),
                          completedBy: userProfile.uid
                        });
                        setEventDialogOpen(false);
                      }}
                    >
                      <CheckCircle sx={{ mr: 1 }} />
                      Mark Complete
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="warning"
                      onClick={async () => {
                        await updateDoc(doc(db, 'events', selectedEvent.id), {
                          status: 'awaiting_approval',
                          submittedAt: new Date().toISOString()
                        });
                        setEventDialogOpen(false);
                      }}
                    >
                      <Warning sx={{ mr: 1 }} />
                      Needs Review
                    </Button>
                  </Box>
                )}
                
                {userProfile?.role === 'child' && selectedEvent.assignedTo.includes(userProfile.uid) && selectedEvent.status !== 'complete' && (
                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button 
                      variant="contained" 
                      color="primary"
                      onClick={async () => {
                        await updateDoc(doc(db, 'events', selectedEvent.id), {
                          status: 'awaiting_approval',
                          submittedAt: new Date().toISOString(),
                          submittedBy: userProfile.uid
                        });
                        setEventDialogOpen(false);
                      }}
                    >
                      <CheckCircle sx={{ mr: 1 }} />
                      Mark as Done
                    </Button>
                    <Button 
                      variant="outlined" 
                      color="secondary"
                      onClick={() => {
                        // TODO: Open photo upload dialog
                        console.log('Open photo upload for task completion');
                      }}
                    >
                      <CameraAlt sx={{ mr: 1 }} />
                      Upload Photo
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Event Details Form */}
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Event Title"
                value={newEvent.title}
                onChange={(e) => setNewEvent({ ...newEvent, title: e.target.value })}
                disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel shrink htmlFor="event-start">Start Time</InputLabel>
                <OutlinedInput
                  id="event-start"
                  label="Start Time"
                  notched
                  type="datetime-local"
                  value={moment(newEvent.start).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => setNewEvent({ 
                    ...newEvent, 
                    start: new Date(e.target.value),
                    end: new Date(new Date(e.target.value).getTime() + 60 * 60 * 1000)
                  })}
                  disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={6}>
              <FormControl fullWidth sx={{ mt: 1 }}>
                <InputLabel shrink htmlFor="event-end">End Time</InputLabel>
                <OutlinedInput
                  id="event-end"
                  label="End Time"
                  notched
                  type="datetime-local"
                  value={moment(newEvent.end).format('YYYY-MM-DDTHH:mm')}
                  onChange={(e) => setNewEvent({ ...newEvent, end: new Date(e.target.value) })}
                  disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>Event Type</InputLabel>
                <Select
                  value={newEvent.eventType}
                  onChange={(e) => setNewEvent({ ...newEvent, eventType: e.target.value })}
                  disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
                  notched
                  label="Event Type"
                >
                  {Object.entries(EVENT_TYPES).map(([type, config]) => (
                    <MenuItem key={type} value={type}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ color: config.color }}>{config.icon}</Box>
                        {config.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel shrink>Assigned To</InputLabel>
                <Select
                  multiple
                  value={newEvent.assignedTo}
                  onChange={(e) => setNewEvent({ ...newEvent, assignedTo: e.target.value })}
                  disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
                  displayEmpty
                  notched
                  label="Assigned To"
                  sx={{ minHeight: 48 }}
                  renderValue={(selected) => (
                    selected.length === 0 ? (
                      <Typography color="text.secondary">Select family members</Typography>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {selected.map((value) => {
                          const member = familyMembers.find((m) => m.id === value);
                          return (
                            <Chip
                              key={value}
                              label={member?.displayName || 'Unknown'}
                              size="small"
                              avatar={
                                <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                                  {member?.displayName?.charAt(0)}
                                </Avatar>
                              }
                            />
                          );
                        })}
                      </Box>
                    )
                  )}
                >
                  {familyMembers.map((member) => (
                    <MenuItem key={member.id} value={member.id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.8rem' }}>
                          {member.displayName?.charAt(0)}
                        </Avatar>
                        {member.displayName}
                        {member.role === 'parent' && (
                          <Chip label="Parent" size="small" color="primary" variant="outlined" />
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            {/* Allowance - only for chores */}
            {newEvent.eventType === 'chore' && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Allowance ($)"
                  type="number"
                  value={newEvent.allowance}
                  onChange={(e) => setNewEvent({ ...newEvent, allowance: parseFloat(e.target.value) || 0 })}
                  inputProps={{ min: 0, step: 0.5 }}
                  InputLabelProps={{ shrink: true }}
                  helperText="Amount earned when this chore is completed"
                  disabled={selectedEvent && userProfile?.role === 'child'}
                />
              </Grid>
            )}
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={4}
                value={newEvent.description}
                onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                disabled={selectedEvent && userProfile?.role === 'child' && selectedEvent.eventType === 'chore'}
              />
            </Grid>

            {/* Recurring Events Section */}
            {userProfile?.role === 'parent' && (
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'grey.50' }}>
                  <CardContent>
                    <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Event />
                      Recurring Event
                    </Typography>
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={newEvent.recurring.enabled}
                          onChange={(e) => setNewEvent({
                            ...newEvent,
                            recurring: { ...newEvent.recurring, enabled: e.target.checked }
                          })}
                        />
                      }
                      label="Make this a recurring event"
                    />
                    
                    {newEvent.recurring.enabled && (
                      <Box sx={{ mt: 2 }}>
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <FormControl fullWidth>
                              <InputLabel>Frequency</InputLabel>
                              <Select
                                value={newEvent.recurring.frequency}
                                onChange={(e) => setNewEvent({
                                  ...newEvent,
                                  recurring: { ...newEvent.recurring, frequency: e.target.value }
                                })}
                              >
                                <MenuItem value="daily">Daily</MenuItem>
                                <MenuItem value="weekly">Weekly</MenuItem>
                                <MenuItem value="monthly">Monthly</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                          
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              label="Every (interval)"
                              type="number"
                              value={newEvent.recurring.interval}
                              onChange={(e) => setNewEvent({
                                ...newEvent,
                                recurring: { ...newEvent.recurring, interval: parseInt(e.target.value) || 1 }
                              })}
                              inputProps={{ min: 1, max: 52 }}
                            />
                          </Grid>
                          
                          {newEvent.recurring.frequency === 'weekly' && (
                            <Grid item xs={12}>
                              <Typography variant="subtitle2" gutterBottom>
                                Days of the week:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                                  <Chip
                                    key={day}
                                    label={day}
                                    clickable
                                    color={newEvent.recurring.daysOfWeek.includes(index) ? 'primary' : 'default'}
                                    onClick={() => {
                                      const days = newEvent.recurring.daysOfWeek.includes(index)
                                        ? newEvent.recurring.daysOfWeek.filter(d => d !== index)
                                        : [...newEvent.recurring.daysOfWeek, index];
                                      setNewEvent({
                                        ...newEvent,
                                        recurring: { ...newEvent.recurring, daysOfWeek: days }
                                      });
                                    }}
                                  />
                                ))}
                              </Box>
                            </Grid>
                          )}
                          
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              label="End Date (optional)"
                              type="date"
                              value={newEvent.recurring.endDate ? moment(newEvent.recurring.endDate).format('YYYY-MM-DD') : ''}
                              onChange={(e) => setNewEvent({
                                ...newEvent,
                                recurring: { 
                                  ...newEvent.recurring, 
                                  endDate: e.target.value ? new Date(e.target.value) : null 
                                }
                              })}
                              InputLabelProps={{ shrink: true }}
                            />
                          </Grid>
                        </Grid>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )}

            {/* Related Task Info */}
            {selectedEvent && selectedEvent.relatedChoreId && (
              <Grid item xs={12}>
                <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                  <CardContent>
                    <Typography variant="subtitle2" color="info.main" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Event />
                      Related Task
                    </Typography>
                    <Typography variant="body2">
                      This event is automatically synced with a task. Changes to the task will update this event.
                    </Typography>
                    <Button 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => {
                        // TODO: Navigate to task details
                        console.log('Navigate to task:', selectedEvent.relatedChoreId);
                      }}
                    >
                      View Task Details
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        
        {/* Screen Time Lock Option - show for new events only, parent only */}
        {!selectedEvent && userProfile?.role === 'parent' && (
          <Box sx={{ px: 3, pb: 1 }}>
            <Tooltip
              title={
                !newEvent.start || !newEvent.end
                  ? "Event must have start and end times to enable screen time rules."
                  : "During this event, assigned children's devices will be locked to Earn-E. They can still use Earn-E features but cannot access other apps. Device unlocks automatically when the event ends."
              }
              placement="top"
              arrow
            >
              <span>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newEvent.screenTimeEnabled}
                      onChange={(e) => setNewEvent({ ...newEvent, screenTimeEnabled: e.target.checked })}
                      color="warning"
                      disabled={!newEvent.start || !newEvent.end}
                    />
                  }
                  label={
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        📱 Apply screen time rules during this event
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Locks device to Earn-E app during event. <strong>Android only.</strong>
                      </Typography>
                    </Box>
                  }
                />
              </span>
            </Tooltip>
            {newEvent.screenTimeEnabled && (
              <Alert severity="info" sx={{ mt: 1 }} icon={false}>
                <Typography variant="caption">
                  📌 Screen time rules only work on Android devices with the Earn-E app installed. iOS support coming soon.
                </Typography>
              </Alert>
            )}
          </Box>
        )}

        <DialogActions sx={{ p: 3, pt: 0 }}>
          {selectedEvent && userProfile?.role === 'parent' && (
            <Button onClick={handleDeleteEvent} color="error" startIcon={<Delete />}>
              Delete Event
            </Button>
          )}
          <Box sx={{ flexGrow: 1 }} />
          <Button onClick={() => setEventDialogOpen(false)}>
            {selectedEvent ? 'Close' : 'Cancel'}
          </Button>
          {(!selectedEvent || userProfile?.role === 'parent' || selectedEvent.eventType !== 'chore') && (
            <Button onClick={handleSaveEvent} variant="contained">
              {selectedEvent ? 'Update Event' : 'Create Event'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Add Event FAB */}
      <Fab
        color="primary"
        aria-label="add event"
        onClick={() => {
          setSelectedEvent(null);
          setNewEvent({
            title: '',
            start: new Date(),
            end: new Date(Date.now() + 60 * 60 * 1000),
            eventType: 'chore',
            assignedTo: [],
            description: '',
            status: 'pending',
            relatedChoreId: null,
            recurring: {
              enabled: false,
              frequency: 'weekly',
              interval: 1,
              endDate: null,
              daysOfWeek: []
            }
          });
          setEventDialogOpen(true);
        }}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          zIndex: 1200
        }}
      >
        <Add />
      </Fab>
      
      {/* Toast/Snackbar notifications */}
      <Snackbar
        open={toast.open}
        autoHideDuration={6000}
        onClose={() => setToast({ ...toast, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setToast({ ...toast, open: false })}
          severity={toast.severity}
          sx={{ width: '100%' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default CalendarPage;